import React, { useState, useEffect, useCallback } from "react";
import { setTitle } from "../../helpers/MetaTag";
import Select from "../../components/form/Select";
import Search from "../../components/form/Search";
import Table from "../../components/elements/table/Table";
import moment from "moment";
import { DragDropContext } from "react-beautiful-dnd";
import { reorder } from "../../helpers";
import toastr from "toastr";
import { postData } from "../../services/api";
import { useTranslation } from "react-i18next";
import Pagination from "../../components/Common/Pagination";
import { getStatusFilter } from "../../constant/Constant";
import Button from "../../components/form/Button";
import BoardMemberFieldOptionPopup from "../../components/popup/BoardMemberFieldOptionPopup";

const BoardMemberFieldOptions = () => {
  setTitle("Board Member Field Options Management");
  const { t } = useTranslation();
  const status_filter = getStatusFilter(t);

  // Tab Management
  const [activeTab, setActiveTab] = useState("study");
  
  // Common States
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortValue, setSortValue] = useState(-1);
  const [sortQuery, setSortQuery] = useState("");
  const [status, setStatus] = useState("");
  const [recordTypeFilter, setRecordTypeFilter] = useState(""); // New filter for record type
  const [noOfPage, setNoOfPage] = useState(0);
  const [fieldOptionData,setFieldOptionData] = useState({});
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [totalCompanyRecord,setTotalCompanyRecord] = useState(0);
  const [startDataCount, setStartDataCount] = useState(0);
  const [endDataCount, setEndDataCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  // Data States for each section
  const [studyData, setStudyData] = useState([]);
  const [industryData, setIndustryData] = useState([]);
  const [expertiseData, setExpertiseData] = useState([]);
  const [modalOpen, setModalOpen] = useState({open: false,data: null });
  const [actionValue, setActionValue] = useState({});

  // Tab Configuration
  const tabs = [
    { 
      key: "study", 
      label: "Study", 
      icon: "fa-graduation-cap",
      apiEndpoint: "filter-item",
      apiExtraPayload: {
        //status: 1,
        type: "study"
      },
      data: studyData,
      setData: setStudyData
    },
    { 
      key: "industry", 
      label: "Industry", 
      icon: "fa-industry",
      apiEndpoint: "industry",
      apiExtraPayload: {
        //status: 1
      },
      data: industryData,
      setData: setIndustryData
    },
    { 
      key: "expertise", 
      label: "Area of Expertise", 
      icon: "fa-star",
      apiEndpoint: "area-of-expertise",
      apiExtraPayload: {
        //status: 1
      },
      data: expertiseData,
      setData: setExpertiseData
    }
  ];

  const currentTab = tabs.find(tab => tab.key === activeTab);

  const extraData = [
    {
      _id: 1,
      label: t("edit"),
      icon: "fa-pen-to-square",
      link: "/",
      type: "button",
      standout: false,
      buttonType: "field_edit",
      isVisabled: 1,
      ordering: 1,
    }
  ]

  useEffect(() => {
    if (sortKey && sortValue) {
      setSortQuery({ [sortKey]: sortValue });
    } else {
      setSortQuery("");
    }
  }, [sortKey, sortValue]);

  // Reset pagination when tab changes
  useEffect(() => {
    setOffset(0);
    setCurrentPage(0);
    setKeyword("");
    setStatus("");
    setRecordTypeFilter(""); // Reset record type filter
    setModalOpen({ open: false, data: null }); 
  }, [activeTab]);

  useEffect(() => {
    if (!actionValue || Object.keys(actionValue).length === 0) return;    
    const { button_type, row_id, current_status } = actionValue; 

    const clearAction = () => {
      setActionValue({});
    };
    
    if (button_type === "field_edit") {
      const record = fieldOptionData.find(item => item?._id === row_id);
      setModalOpen({
        open: true,
        data: {
          row_id,
          name: record?.name ?? "",
        },
      });
      clearAction();
      return;
    }
    
    // Unified async handler for status and delete actions
    const handleAction = async (type) => {
      try {
        const paths = {
          status: {
            study: "filter-item/status-change",
            expertise: "filter-item/status-change", 
            industry: "industry/status-change"
          },
          delete: {
            study: "filter-item/delete",
            expertise: "filter-item/delete",
            industry: "industry/delete"
          }
        };

        const payload = type === "status"
          ? { id: row_id, status: current_status }
          : { id: row_id };
        
        const apiPath = paths[type]?.[currentTab?.key];
        if (!apiPath) {
          throw new Error(`Invalid action type: ${type} for tab: ${currentTab?.key}`);
        }
        const result = await postData(apiPath, payload);
        if (result.status === 200 || result.data) {
          toastr.success(result.message || `${type} action completed successfully`);
          loadList();
        } else {
          toastr.error(result.message || `${type} action failed`);
        }
      } catch (error) {
        console.error(`${type} action error:`, error);
        toastr.error(error.message || `Failed to perform ${type} action`);
      } finally {
        clearAction();
      }
    };
    
    if (button_type === "status" || button_type === "delete") {
      handleAction(button_type);
    }

  }, [actionValue, fieldOptionData, currentTab]);


  // Load data for current tab
  const loadList = useCallback(async () => {
    const loadData = async () => {
      setLoading(true);
      try {
        const result = await postData(`${currentTab.apiEndpoint}/list`, {
          keyword: keyword,
          limit: limit,
          offset: offset,
          sortQuery: sortQuery,
          status: status,
          recordType: recordTypeFilter, // Add record type filter
          ...currentTab.apiExtraPayload
        });
        
        if (result.data) {
          setFieldOptionData(result.data)
          const formattedData = result.data.map((value) => ({
            _id: value?._id,
            rowData: [
              { _id: 1, width: 35, type: "text", data: value?.name },
              {
                _id: 2,
                width: 20,
                type: "text",
                data: value?.company_id ? (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <i className="fas fa-building text-xs mr-1"></i>
                      {value?.company_name || "Company Record"}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <i className="fas fa-globe text-xs mr-1"></i>
                      Global
                    </span>
                  </div>
                )
              },
              {
                _id: 3,
                width: 20,
                type: "text",
                data: value?.created_at
                  ? moment(new Date(value?.created_at)).format("Do MMM YYYY")
                  : "N/A",
              },
              {
                _id: 4,
                width: 15,
                type: "status",
                statusLabel: value?.status === 1 ? t("active") : t("deactivated"),
                statusType: value?.status === 1 ? "success" : "warning",
              },
              { 
                _id: 5, 
                width: 10, 
                type: "action",
                statusLabel: value?.status === 1 ? t("active") : t("deactivated"),
                statusType: value?.status === 1 ? "success" : "warning",
                data : [
                  { field_edit: 1 },
                ]
              }
            ],
          }));          
          currentTab.setData(formattedData);
        }
        setTotalDataCount(result.count || 0);
        setTotalCompanyRecord(result.total_company_record)
        setActionValue({});
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.log(err.message);
        toastr.error("Failed to load data");
      }
    };

    if (currentTab) {
      loadData();
    }
  }, [activeTab, keyword, limit, offset, sortQuery, status, recordTypeFilter, t]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    setNoOfPage(Math.ceil(totalDataCount / limit));
    const startItem = Number(totalDataCount > 0 ? 1 : 0);
    const endItem = Math.min(startItem + limit - 1, totalDataCount);
    setStartDataCount(startItem);
    setEndDataCount(endItem);
  }, [limit, totalDataCount]);

  const handlePageClick = (event) => {
    const newOffset = (event.selected * limit) % totalDataCount;
    setCurrentPage(event.selected);
    setOffset(newOffset);
    const startItem = Number(newOffset + 1);
    const endItem = Math.min(startItem + limit - 1, totalDataCount);
    setStartDataCount(startItem);
    setEndDataCount(endItem);
  };

  const tableHeadData = [
    {
      _id: 1,
      width: 35,
      name: t("name"),
      value: "name",
      align: "left",
      isSort: true,
      isFilter: false,
    },
    {
      _id: 2,
      width: 20,
      name: "Record Type",
      value: "company_id",
      align: "left",
      isSort: false,
      isFilter: true,
      filterData: [
        { _id: "global", label: "Global Records", value: null },
        { _id: "company", label: "Company Records", value: "company" }
      ],
      onFilter: (data) => {
        if (data && Array.isArray(data) && data.length === 1) {
          const filterValue = data[0]?.value;
          setRecordTypeFilter(filterValue);
        } else {
          setRecordTypeFilter("");
        }
        setOffset(0);
        setCurrentPage(0);
      },
    },    
    {
      _id: 3,
      width: 20,
      name: t("created_date"),
      value: "created_at",
      align: "left",
      isSort: true,
      isFilter: false,
    },
    {
      _id: 4,
      width: 15,
      name: t("status"),
      value: "status",
      align: "left",
      isSort: false,
      isFilter: true,
      filterData: status_filter || [],
      onFilter: (data) => {
        if (data && Array.isArray(data) && data.length === 1) {
          setStatus(data[0]?.value);
        } else {
          setStatus("");
        }
        setOffset(0);
        setCurrentPage(0);
      },
    },
    {
      _id: 5,
      width: 10,
      name: t("action"),
      value: "",
      align: "right",
      isSort: false,
      isFilter: false,
    },
  ];

  const handleOnDragEnd = async (result) => {
    if (!result.destination) return;

    const items = reorder(
      currentTab.data,
      result.source.index,
      result.destination.index
    );
    currentTab.setData(items);

    try {
      const res = await postData(`${currentTab.apiEndpoint}/ordering`, {
        items: items,
      });
      if (res.status === 200) {
        toastr.success(res.message || "Order updated successfully");
      } else {
        toastr.error(res.message || "Failed to update order");
      }
    } catch (error) {
      toastr.error("Failed to update order");
    }
  };
  

  return (
    <>
    <div className="border bg-white border-gray-200 p-4 shadow-md rounded-xl">
      {/* Header */}
      <div className="relative flex justify-between items-center mb-6 flex-wrap sm:flex-nowrap gap-y-4">
        <div className="flex flex-col">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Board Member Field Options</h1>
          <p className="text-sm text-gray-500">Manage study areas, industries, and expertise fields for board members</p>
        </div>
        <Button
          buttonType="button"
          isCustomLabel={true}
          customData={
            <>
              <i className="fas fa-plus text-xs mr-1.5"></i>
              <span className="text-sm font-medium">Add {currentTab?.label}</span>
            </>
          }
          buttonClasses="!rounded-full !bg-gradient-to-r !from-teal-500 !to-emerald-500 hover:!from-teal-600 hover:!to-emerald-600 !text-white !border-0 !px-4 !py-2 !text-sm !shadow-md hover:!shadow-lg !transition-all !duration-200 !transform hover:!scale-105"
          functions={() => {setModalOpen({ open: true, data: null }); }}
        />
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${
                activeTab === tab.key
                  ? "border-teal-500 text-teal-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <i className={`fas ${tab.icon}`}></i>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Current Tab Info with Record Type Summary */}
      <div className="mb-4 p-3 bg-teal-50 rounded-lg border border-teal-200">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <i className={`fas ${currentTab?.icon} text-teal-600`}></i>
            <span className="font-medium text-teal-800">
              Managing: {currentTab?.label}
            </span>
            <span className="text-teal-600 text-sm">
              ({totalDataCount} total items)
            </span>
          </div>
          
          {/* Record Type Summary */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <i className="fas fa-globe text-xs mr-1"></i>
                Global
              </span>
              <span className="text-gray-600">
                ({Number(totalDataCount-totalCompanyRecord) || 0})
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <i className="fas fa-building text-xs mr-1"></i>
                Company
              </span>
              <span className="text-gray-600">
                ({totalCompanyRecord || 0})
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="relative flex gap-3 items-center mb-4 flex-wrap">
        <div className="relative">
          <Select
            xPlacement="left"
            dropdownButtonClass="!bg-white"
            selectedValue={limit}
            dropdownData={[
              { _id: 1000000, label: t("all_items"), value: 1000000 },
              { _id: 10, label: "10 " + t("items"), value: 10 },
              { _id: 20, label: "20 " + t("items"), value: 20 },
              { _id: 30, label: "30 " + t("items"), value: 30 },
              { _id: 50, label: "50 " + t("items"), value: 50 },
              { _id: 100, label: "100 " + t("items"), value: 100 },
            ]}
            getSelectedValue={(e) => {
              setLimit(e.value);
              setOffset(0);
              setCurrentPage(0);
            }}
          />
        </div>
        <div className="relative w-48">
          <Search
            placeholder={`Search ${currentTab?.label}...`}
            setInput={setKeyword}
            valueReset={() => {
              setOffset(0);
              setCurrentPage(0);
            }}
          />
        </div>
      </div>

      {/* Table */}
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <div className="w-full bg-white rounded-md shadow overflow-hidden">
          <Table
            tableData={currentTab?.data || []}
            tableHeadData={tableHeadData}
            totalRecord={totalDataCount}
            loading={loading}
            getSortValue={setSortValue}
            getSortKey={setSortKey}
            isStatusUpdate={true}
            isDeleteEnabled={true}
            editUrlParams={`?type=${activeTab}`}
            isDraggable={false}
            tableImage={false}
            extraData={extraData}
            getActionValue={(obj) => setActionValue(obj)}
          />
        </div>
      </DragDropContext>

      {/* Pagination */}
      {noOfPage > 1 ? (
        <Pagination
          handlePageClick={handlePageClick}
          noOfPage={noOfPage}
          startDataCount={startDataCount}
          endDataCount={endDataCount}
          count={totalDataCount}
          forcePage={currentPage}
        />
      ) : (
        <div className="flex justify-center sm:justify-between my-4 text-sm text-black text-center sm:text-left">
          {totalDataCount > 0 ? (
            <div>
              <p>
                {t("showing")}: {startDataCount} to {endDataCount} of{" "}
                {totalDataCount}
              </p>
            </div>
          ) : (
            ""
          )}
        </div>
      )}
    </div>
    <BoardMemberFieldOptionPopup
      isOpen={modalOpen?.open}
      setIsOpen={setModalOpen}
      label={currentTab?.label}
      fieldKey={currentTab?.key}
      data={modalOpen?.data}
      loadList={loadList}
    />
    </>
  );
};

export default BoardMemberFieldOptions;